import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

type SenderSearchParams = {
  /** Agnostic theme hides certain buttons to create a minimal UI for embedding in an iFrame */
  theme?: 'agnostic' | 'default';
  /** Skip the sent page after sending an envelope */
  skipSentPage?: boolean;
  /** Show the Seller's Shield banner on the envelope sent page (passed from files-ui) */
  showSellersShieldBanner?: boolean;
};

/**
 * Returns the current search params from the URL as an object, with basic
 * boolean coercion applied.
 *
 * This is used by Prime when hosting DigiSign in an iFrame to control
 * the theme/branding (via the `theme` param) and behavior such as
 * `skipSentPage`, but it may also include any other query parameters
 * present in the URL.
 *
 * Values of `"true"` and `"false"` are converted to booleans; all other
 * values remain strings.
 *
 * @returns An object containing all current search params from the URL.
 */
export function useSearchParams(): SenderSearchParams {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const entries = Array.from(params.entries()).map(([key, value]) => {
      if (value === 'true') return [key, true];
      if (value === 'false') return [key, false];
      return [key, value];
    });
    return Object.fromEntries(entries) as SenderSearchParams;
  }, [location.search]);
}
